"use client";

import { useEffect } from "react";
import { AlertCircle, ArrowLeft } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import * as Sentry from "@sentry/nextjs";
import { LockIconSvg } from "./icons/lock-icon";
import { useTranslation } from "@/lib/sajilni-localization";

interface ErrorPageProps {
  error?: Error & { digest?: string };
  reset?: () => void;
  title?: string;
  message?: string;
  showReset?: boolean;
  showBack?: boolean;
  isPermissionError?: boolean;
  showErrorDetails?: boolean;
}

export default function ErrorPage({
  error,
  reset,
  title = "We'll be back soon",
  message,
  showReset = true,
  showBack = true,
  isPermissionError = false,
  showErrorDetails = false,
}: ErrorPageProps) {
  const { t } = useTranslation("error");

  useEffect(() => {
    if (error) {
      Sentry.captureException(error);
      console.error("Error:", error);
    }
  }, [error]);

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 py-8">
      <div className="flex w-full max-w-md flex-col items-center text-center">
        <div className="relative mb-6 h-48 w-48">
          {isPermissionError ? (
            <LockIconSvg />
          ) : (
            <Image
              src="/content-unavailable.svg"
              width={228}
              height={203}
              className="h-full w-full"
              alt="Content unavailable"
              priority
            />
          )}
        </div>

        <h1 className="mb-3 whitespace-nowrap text-2xl font-semibold">
          {t(title)}
        </h1>

        {message && !isPermissionError && (
          <p className="mb-8 whitespace-nowrap text-gray-600">{t(message)}</p>
        )}
        {showErrorDetails && error && (
          <div className="mb-6 w-full rounded-md border border-gray-200 bg-gray-50 p-4 text-left">
            <p className="mb-1 flex items-center text-sm font-medium text-red-600">
              <AlertCircle className="mr-1 h-4 w-4" />
              {t("Error Details (Development Only)")}
            </p>
            <p className="break-words text-sm text-gray-700">{error.message}</p>
            {error.digest && (
              <p className="mt-2 text-xs text-gray-500">
                {t("Digest:")} {error.digest}
              </p>
            )}
          </div>
        )}

        <div className="mt-4 flex justify-center">
          {showBack && (
            <Link
              href="/"
              className="mt-6 flex items-center gap-2 rounded-md border border-gray-200 px-4 py-1 text-sm transition duration-300"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("Go Back")}
            </Link>
          )}

          {/* {showReset && reset && (
            <button
              onClick={() => reset()}
              className="ml-3 flex items-center justify-center gap-2 rounded-md bg-blue-600 px-5 py-2.5 text-white transition-colors hover:bg-blue-700"
            >
              Try Again
            </button>
          )} */}
        </div>
      </div>
    </div>
  );
}
